import type { SubmitEventHandler } from 'react';
import type { ContentObjectInternalProps } from '../../lib/flotiq-client/types/api-response';

import { useStore } from '@tanstack/react-form';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { z } from 'zod';

import { getTestProps } from '../../lib/helpers';

import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { ClockIcon, InformationSignIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import { getTimezonesOptions } from './timezones';

const timezones = getTimezonesOptions();

interface SchedulePublicationFormProps {
  /**
   * Content object that should be scheduled for publication.
   */
  initialData?: ContentObjectInternalProps;
  /**
   * Optional test ID for testing purposes.
   */
  testId?: string;
}

const SchedulePublicationForm = ({
  initialData,
  testId,
}: SchedulePublicationFormProps) => {
  const { t } = useTranslation();
  const modalInstance = useContext(ModalInstanceContext);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialValues = useMemo(() => {
    const initialTimezone = initialData?.scheduledAtTimeZone || timezone;
    const dateTime = momentTz.tz(initialData?.publishedAt, initialTimezone);
    return {
      date: dateTime.format('YYYY-MM-DD'),
      time: dateTime.format('HH:mm'),
      timezone: initialTimezone,
    };
  }, [initialData?.publishedAt, initialData?.scheduledAtTimeZone, timezone]);

  const closeModal = useCallback(() => {
    modalInstance.resolve?.();
  }, [modalInstance]);

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      const values = {
        datetime: momentTz
          .tz(`${value.date}T${value.time}`, value.timezone)
          .format(),
        timezone: value.timezone,
      };
      modalInstance.resolve?.(values);
    },
    validators: {
      onChange: z.object({
        date: z.string().min(1, t('ContentForm.Errors.Required')),
        time: z.string().min(1, t('ContentForm.Errors.Required')),
        timezone: z.string().min(1, t('ContentForm.Errors.Required')),
      }),
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const { values: formValues, isTouched } = useStore(form.store, (state) => ({
    values: state.values,
    isTouched: state.isTouched,
  }));

  const isDateInvalid = useMemo(() => {
    if (!formValues.date || !formValues.time || !formValues.timezone) {
      return false;
    }

    const currentDate = momentTz();
    const chosenDate = momentTz.tz(
      `${formValues.date} ${formValues.time}`,
      formValues.timezone,
    );

    return moment(chosenDate).isBefore(moment(currentDate).add(5, 'minutes'));
  }, [formValues.date, formValues.time, formValues.timezone]);

  return (
    <form.AppForm>
      <form
        className="max-w-4xl space-y-3"
        id="schedule-form"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div className="flex gap-3 w-full">
          <form.AppField name="date">
            {(field) => (
              <field.Input
                label={t('PublicationSchedule.Date')}
                type="date"
                required
                {...getTestProps(testId, 'date', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="time">
            {(field) => (
              <field.Input
                label={t('PublicationSchedule.Time')}
                type="time"
                required
                {...getTestProps(testId, 'time', 'testId')}
              />
            )}
          </form.AppField>
        </div>

        <form.AppField name="timezone">
          {(field) => (
            <field.Dropdown
              label={t('PublicationSchedule.Timezone')}
              options={timezones}
              required
              isVirtualScrolling
              {...getTestProps(testId, 'timezone', 'testId')}
            />
          )}
        </form.AppField>

        {isDateInvalid && isTouched && (
          <div
            className="text-red text-sm mt-10 leading-none text-left"
            {...getTestProps(testId, 'invalid-error', 'testId')}
          >
            {t('PublicationSchedule.InvalidDate')}
          </div>
        )}

        <form.Subscribe selector={({ values }) => values.timezone}>
          {(selectedTimezone) =>
            selectedTimezone !== timezone && (
              <div className="flex items-start gap-2 text-blue dark:text-blue-300 mt-3">
                <InformationSignIcon className="w-5 mt-1" />
                {t('PublicationSchedule.TimezoneDate', {
                  date: momentTz
                    .tz(
                      `${formValues.date}T${formValues.time}`,
                      selectedTimezone,
                    )
                    .tz(timezone)
                    .format('YYYY-MM-DD HH:mm'),
                  timezone: timezones.find((tz) => tz.value === timezone)
                    ?.label,
                })}
              </div>
            )
          }
        </form.Subscribe>

        <div
          className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
                border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-lg"
        >
          <CancelButton onClick={closeModal} />
          <SaveButton
            form="schedule-form"
            iconImage={<ClockIcon className="w-3 min-w-3" />}
            label={t('PublicationSchedule.SetSchedule')}
            disabled={isDateInvalid}
            {...getTestProps(testId, 'save', 'testId')}
          />
        </div>
      </form>
    </form.AppForm>
  );
};

export default SchedulePublicationForm;
